import { useState, useEffect, useRef } from 'react';
import { usePageTitle } from '../appInfo';
import useCoarsePointer from '../components/useCoarsePointer';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/api';
import { DUEL_STATUSES, STATUS_LABEL } from '../utils/duelStatuses';
import HandExportPanel from '../components/HandExportPanel';
import FixChatPanel from '../components/FixChatPanel';
import { formatServerDate } from '../utils/serverTime';

function PlayerCard({ info, deckInfo, onDeckClick }) {
  return (
    // 2枚が VS を挟んで並ぶので、**縮める余地を持たせる**（`flex: 1` ＋ `minWidth: 0`）。
    // 無いと名前やデッキ名が長いときに縮まず、カードごと押し出して横に溢れる
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
      {info?.image_thumbnail_url
        ? <img src={info.image_thumbnail_url} alt={info.kanji} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        : <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#333', flexShrink: 0 }} />
      }
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {info?.kanji || info?.name || '—'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, minWidth: 0 }}>
          {deckInfo?.deck_illust_thumbnail_url &&
            <img src={deckInfo.deck_illust_thumbnail_url} alt={deckInfo.name} style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover' }} />
          }
          <span
            style={{ fontSize: '0.85rem', color: onDeckClick && deckInfo?.id ? '#7ab' : '#aaa', cursor: onDeckClick && deckInfo?.id ? 'pointer' : 'default', textDecoration: onDeckClick && deckInfo?.id ? 'underline' : 'none',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            onClick={() => onDeckClick && deckInfo?.id && onDeckClick(deckInfo.id)}
          >
            {deckInfo?.name ?? 'N/A'}
          </span>
        </div>
      </div>
    </div>
  );
}

function DeckViewModal({ deckId, onClose }) {
  // 指のときは10列に潰さない（358px 幅だと1枚 33px になり何のカードか読めない）。
  // 段の決め方は DeckManagement のデッキ編集と揃えてある
  const coarse = useCoarsePointer();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!deckId) return;
    setData(null);
    api.project('GetDeckCards', { deck_id: deckId }).then(setData);
  }, [deckId]);

  if (!deckId) return null;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, padding: 20,
          // **頭打ちの相手は `maxWidth` と同じ 85vw にすること。** CSS では
          // min-width が max-width に必ず勝つので、350px の端末では
          // 85vw（=297.5px）より min-width の 300px が優先されて溢れる。
          // `100%` で頭打ちにしても直らない（入れ物は画面いっぱいなので 300 のまま）
          maxWidth: '85vw', maxHeight: '85vh', overflow: 'auto', minWidth: 'min(300px, 85vw)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{data?.deck_name ?? '読み込み中...'}{data && <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: 8 }}>({data.cards.length}枚)</span>}</h3>
          <button className="btn btn-sm btn-secondary" onClick={onClose}>✕</button>
        </div>
        {!data ? (
          <p style={{ color: '#666' }}>読み込み中...</p>
        ) : (
          <div className="no-callout" style={{ display: 'grid', gridTemplateColumns: coarse ? 'repeat(auto-fill, minmax(64px, 1fr))' : 'repeat(10, 1fr)', gap: 3 }}>
            {data.cards.map((card, i) => (
              <div key={i} title={card.card_name}>
                {card.thumbnail_url
                  ? <img src={card.thumbnail_url} alt={card.card_name} style={{ width: '100%', display: 'block', borderRadius: 2 }} />
                  : <div style={{ aspectRatio: '59/80', background: '#333', borderRadius: 2 }} />
                }
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

/**
 * ソースファイルのアップローダ。
 * 試合単位（duel_games.sources / Duels/{duel_id}/{game_id}/sources/）と
 * デュエルシリーズ単位（duels.sources / Duels/{duel_id}/sources/、Issue #126）の
 * 両方で使う。違いは s3 キーのプレフィックスと呼び出すアクションだけなので、
 * それらを props で受け取る。
 *
 * @param sources         カンマ区切りの s3_key 文字列（duel_games.sources / duels.sources）
 * @param prefix          一覧表示時に s3_key から取り除くプレフィックス
 * @param requestUploadUrls (filenames) => Promise<[{upload_link, s3_key, filename}, ...]>
 * @param createMultipart   (filename)  => Promise<{upload_id, s3_key}>
 */
function SourceUpload({ sources, prefix, requestUploadUrls, createMultipart, onUploaded }) {
  const coarse = useCoarsePointer();
  const [selectedFiles, setSelectedFiles] = useState([]); // { file, relativePath }
  const [uploading, setUploading] = useState(false);
  const [progresses, setProgresses] = useState({});       // relativePath → 0-100
  const folderInputRef = useRef(null);
  const fileInputRef   = useRef(null);

  const existingSources = sources
    ? sources.split(',').filter(s => s.trim()).map(s => s.replace(prefix, ''))
    : [];

  const handleSelect = (e, useRelative) => {
    const files = Array.from(e.target.files ?? []);
    const entries = files.map(f => ({
      file: f,
      relativePath: useRelative && f.webkitRelativePath ? f.webkitRelativePath : f.name,
    }));
    setSelectedFiles(entries);
    setProgresses({});
    e.target.value = '';
  };

  const MULTIPART_THRESHOLD = 5 * 1024 ** 3;  // 5 GB
  const CHUNK_SIZE          = 100 * 1024 ** 2; // 100 MB

  const uploadSingle = (uploadUrl, file, onProgress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) onProgress(ev.loaded / ev.total);
      };
      xhr.onload  = () => xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`));
      xhr.onerror = () => reject(new Error('アップロード失敗'));
      xhr.send(file);
    });

  const uploadMultipart = async (s3Key, uploadId, file, onProgress) => {
    const totalParts = Math.ceil(file.size / CHUNK_SIZE);
    const parts = [];
    let uploadedBytes = 0;

    for (let partNo = 1; partNo <= totalParts; partNo++) {
      const start  = (partNo - 1) * CHUNK_SIZE;
      const chunk  = file.slice(start, start + CHUNK_SIZE);
      const { url } = await api.project('GetMultipartPartUrl', { s3_key: s3Key, upload_id: uploadId, part_number: partNo });

      const etag = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            onProgress((uploadedBytes + ev.loaded) / file.size);
          }
        };
        xhr.onload = () => {
          if (xhr.status < 300) {
            uploadedBytes += chunk.size;
            onProgress(uploadedBytes / file.size);
            resolve(xhr.getResponseHeader('ETag'));
          } else {
            reject(new Error(`HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('パートアップロード失敗'));
        xhr.send(chunk);
      });

      parts.push({ PartNumber: partNo, ETag: etag });
    }

    const res = await api.project('CompleteMultipartUpload', { s3_key: s3Key, upload_id: uploadId, parts });
    if (res.error) throw new Error(res.error);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    try {
      // 小さいファイルは既存 API で一括 URL 取得、大きいファイルは個別マルチパート
      const small = selectedFiles.filter(e => e.file.size <= MULTIPART_THRESHOLD);
      const large = selectedFiles.filter(e => e.file.size > MULTIPART_THRESHOLD);

      // 小ファイル: 既存フロー
      if (small.length > 0) {
        const urlList = await requestUploadUrls(small.map(e => e.relativePath));
        await Promise.all(urlList.map((info, i) =>
          uploadSingle(info.upload_link, small[i].file, (ratio) =>
            setProgresses(prev => ({ ...prev, [small[i].relativePath]: Math.round(ratio * 100) }))
          ).then(() =>
            setProgresses(prev => ({ ...prev, [small[i].relativePath]: 100 }))
          )
        ));
      }

      // 大ファイル: マルチパート
      for (const { file, relativePath } of large) {
        const { upload_id, s3_key } = await createMultipart(relativePath);
        await uploadMultipart(s3_key, upload_id, file, (ratio) =>
          setProgresses(prev => ({ ...prev, [relativePath]: Math.round(ratio * 100) }))
        );
        setProgresses(prev => ({ ...prev, [relativePath]: 100 }));
      }

      setSelectedFiles([]);
      setProgresses({});
      onUploaded();
    } catch (err) {
      alert('アップロード失敗: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const totalSize = selectedFiles.reduce((s, e) => s + e.file.size, 0);

  return (
    <div>
      {/* 既存ソース */}
      {existingSources.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: 6 }}>アップロード済み ({existingSources.length}件)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 140, overflow: 'auto' }}>
            {existingSources.map((path, i) => (
              <div key={i} style={{ fontSize: '0.75rem', color: '#888', fontFamily: 'monospace', padding: '1px 4px', background: '#141414', borderRadius: 3 }}>
                {path}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ファイル選択ボタン */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input ref={folderInputRef} type="file" multiple webkitdirectory="" style={{ display: 'none' }}
          onChange={e => handleSelect(e, true)} />
        <input ref={fileInputRef}   type="file" multiple style={{ display: 'none' }}
          onChange={e => handleSelect(e, false)} />
        {/* webkitdirectory は iOS Safari / WKWebView で未対応。押しても
            何も起きないので、指のときは出さない（隣の複数ファイル選択は動く） */}
        {!coarse && (
          <button className="btn btn-sm btn-secondary" disabled={uploading}
            onClick={() => folderInputRef.current?.click()}>
            フォルダを選択
          </button>
        )}
        <button className="btn btn-sm btn-secondary" disabled={uploading}
          onClick={() => fileInputRef.current?.click()}>
          ファイルを選択
        </button>
      </div>

      {/* 選択済みファイル一覧 */}
      {selectedFiles.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: 6 }}>
            選択中: {selectedFiles.length}件 / {formatBytes(totalSize)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 200, overflow: 'auto' }}>
            {selectedFiles.map(({ file, relativePath }) => {
              const pct = progresses[relativePath] ?? null;
              return (
                <div key={relativePath} style={{ fontSize: '0.72rem', fontFamily: 'monospace', background: '#141414', borderRadius: 3, padding: '3px 6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: pct === 100 ? '#4a9' : '#888' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{relativePath}</span>
                    <span style={{ marginLeft: 8, flexShrink: 0, color: '#555' }}>{formatBytes(file.size)}</span>
                    {pct !== null && <span style={{ marginLeft: 8, flexShrink: 0, color: '#7ab' }}>{pct}%</span>}
                  </div>
                  {pct !== null && pct < 100 && (
                    <div style={{ height: 2, background: '#222', borderRadius: 1, marginTop: 2 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: '#4a8', borderRadius: 1, transition: 'width 0.1s' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleUpload}
            disabled={uploading}
            style={{ marginTop: 8 }}
          >
            {uploading ? 'アップロード中...' : `${selectedFiles.length}件をアップロード`}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * デュエルシリーズ共通ソース（Issue #126）。
 * どの試合にも属さない素材（OP/ED素材・共通BGM・全試合を通しで撮った引きの映像等）を
 * Duels/{duel_id}/sources/ に置く。出力フォルダへ落とすワーカー（duel_sources）は Issue #756 で消した。
 */
function DuelSeriesSourceCard({ duel, duelId, onUploaded }) {
  return (
    <div className="card">
      <h2 className="card-title" style={{ margin: '0 0 4px' }}>シリーズ共通ソースファイル</h2>
      <p style={{ fontSize: '0.8rem', color: '#555', marginBottom: 12 }}>
        試合に属さない、シリーズ全体で使う素材（OP/ED素材・共通BGM・通しで撮った引きの映像など）。
        フォルダ構造ごとアップロードできます。
      </p>
      <SourceUpload
        sources={duel?.sources}
        prefix={`Duels/${duelId}/sources/`}
        requestUploadUrls={(filenames) =>
          api.project('UploadDuelSeriesSources', { duel_id: duelId, filenames })}
        createMultipart={(filename) =>
          api.project('CreateMultipartDuelSeriesUpload', { duel_id: duelId, filename })}
        onUploaded={onUploaded}
      />
    </div>
  );
}

function MovieSlot({ index, game, duelId, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  const s3Key   = game?.[`movie${index}_url`] ?? null;
  const dlUrl   = game?.[`movie${index}_download_url`] ?? null;
  const filename = s3Key ? s3Key.split('/').pop() : null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const res = await api.project('UploadDuelMovie', {
        duel_id: duelId,
        game_id: game.id,
        movie_index: index,
        filename: file.name,
      });
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', res.upload_link);
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgress(Math.round(ev.loaded / ev.total * 100));
        };
        xhr.onload  = () => xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`));
        xhr.onerror = () => reject(new Error('アップロード失敗'));
        xhr.send(file);
      });
      onUploaded();
    } catch (err) {
      alert('アップロード失敗: ' + err.message);
    } finally {
      setUploading(false);
      setProgress(null);
      e.target.value = '';
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 0', borderBottom: '1px solid #1e1e1e',
    }}>
      <span style={{ fontSize: '0.85rem', color: '#777', minWidth: 48 }}>動画 {index}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {filename ? (
          <a href={dlUrl ?? '#'} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: '0.85rem', color: '#7ab', textDecoration: 'none', wordBreak: 'break-all' }}>
            {filename}
          </a>
        ) : (
          <span style={{ fontSize: '0.8rem', color: '#444' }}>未アップロード</span>
        )}
        {uploading && progress !== null && (
          <div style={{ marginTop: 4, height: 4, background: '#222', borderRadius: 2 }}>
            <div style={{ width: `${progress}%`, height: '100%', background: '#4a8', borderRadius: 2, transition: 'width 0.2s' }} />
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={handleFile} />
      <button
        className="btn btn-sm btn-secondary"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
      >
        {uploading ? `${progress ?? 0}%` : filename ? '差し替え' : 'アップロード'}
      </button>
    </div>
  );
}

export default function DuelEdit() {
  // デッキのカード一覧を指のときに潰さない（DeckViewModal と同じ扱い）
  const coarse = useCoarsePointer();
  const { duelsId } = useParams();
  const navigate = useNavigate();

  const [duel, setDuel] = useState(null);
  const [name, setName] = useState('');
  usePageTitle(name, 'デュエル');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [decks, setDecks] = useState([]);
  const [deck1Id, setDeck1Id] = useState('');
  const [deck2Id, setDeck2Id] = useState('');
  const [deck1Versions, setDeck1Versions] = useState([]);
  const [deck2Versions, setDeck2Versions] = useState([]);
  const [deck1PinnedId, setDeck1PinnedId] = useState('');
  const [deck2PinnedId, setDeck2PinnedId] = useState('');
  const [savingDecks, setSavingDecks] = useState(false);
  const [deckViewId, setDeckViewId] = useState(null);
  const [deck1PreviewCards, setDeck1PreviewCards] = useState(null);
  const [deck2PreviewCards, setDeck2PreviewCards] = useState(null);
  const [addingGame, setAddingGame] = useState(false);

  const load = () => Promise.all([
    api.project('GetDuelInfoProject', { duel_id: Number(duelsId) }),
    api.project('GetDeckList', { keys: ['id', 'name', 'player', 'parent'] }),
  ]).then(([res, deckList]) => {
    if (res.status !== 'success') return;
    const found = res.duel;
    setDuel(found);
    setName(found.name ?? '');
    setSummary(found.summary ?? '');
    setDescription(found.description ?? '');
    setDecks(deckList ?? []);
    setDeck1Id(found.player1_deck_id ? String(found.player1_deck_id) : '');
    setDeck2Id(found.player2_deck_id ? String(found.player2_deck_id) : '');
    setDeck1PinnedId(found.player1_pinned_deck_id ? String(found.player1_pinned_deck_id) : '');
    setDeck2PinnedId(found.player2_pinned_deck_id ? String(found.player2_pinned_deck_id) : '');
  });

  useEffect(() => { load(); }, [duelsId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!deck1Id) { setDeck1Versions([]); return; }
    api.project('GetDeckVersionList', { parent_id: Number(deck1Id) })
      .then(setDeck1Versions)
      .catch(() => setDeck1Versions([]));
  }, [deck1Id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!deck2Id) { setDeck2Versions([]); return; }
    api.project('GetDeckVersionList', { parent_id: Number(deck2Id) })
      .then(setDeck2Versions)
      .catch(() => setDeck2Versions([]));
  }, [deck2Id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!deck1PinnedId) { setDeck1PreviewCards(null); return; }
    api.project('GetDeckCards', { deck_id: Number(deck1PinnedId) })
      .then(d => setDeck1PreviewCards(d?.cards ?? []))
      .catch(() => setDeck1PreviewCards(null));
  }, [deck1PinnedId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!deck2PinnedId) { setDeck2PreviewCards(null); return; }
    api.project('GetDeckCards', { deck_id: Number(deck2PinnedId) })
      .then(d => setDeck2PreviewCards(d?.cards ?? []))
      .catch(() => setDeck2PreviewCards(null));
  }, [deck2PinnedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.project('UpdateDuel', { duel_id: Number(duelsId), name, summary, description });
    } catch (err) {
      alert('保存に失敗しました: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // デッキ・バージョンを付け替えると、その試合の記録の写しへ移す。記録が止まる試合があれば
  // サーバーが truncate_required で断る → 止まる手から先を消して付け替えるか確かめる（強制して反映は無い。Issue #954）
  const handleSaveDecks = async () => {
    setSavingDecks(true);
    const params = {
      duel_id: Number(duelsId),
      ...(deck1Id && { player1_deck_id: Number(deck1Id) }),
      ...(deck2Id && { player2_deck_id: Number(deck2Id) }),
      player1_pinned_deck_id: deck1PinnedId ? Number(deck1PinnedId) : 0,
      player2_pinned_deck_id: deck2PinnedId ? Number(deck2PinnedId) : 0,
    };
    try {
      let res = await api.projectResult('UpdateDuel', params);
      if (!res.ok && res.body?.truncate_required) {
        const games = (res.body.games ?? []).map((g) => `・${g.game_number} 試合目: ${g.reason}（消える記録 ${g.drop_ops} 手）`).join('\n');
        if (!window.confirm(`この割り当てでは記録が止まる試合があります。\n${games}\n\n止まる手から先の記録を消して付け替えますか？`)) return;
        res = await api.projectResult('UpdateDuel', { ...params, truncate: true });
      }
      if (!res.ok) { alert('デッキ更新に失敗しました: ' + (res.body?.error ?? res.status)); return; }
      if (res.body?.unsynced?.length) {
        alert('記録へ移せなかった試合があります（入力画面を開くと取り込み直します）:\n'
          + res.body.unsynced.map((u) => `・${u.game_number} 試合目: ${u.reason}`).join('\n'));
      }
      await load();
    } catch (err) {
      alert('デッキ更新に失敗しました: ' + err.message);
    } finally {
      setSavingDecks(false);
    }
  };

  const handleAddGame = async () => {
    setAddingGame(true);
    try {
      await api.addDuelGame(Number(duelsId));
      await load();
    } catch (err) {
      alert('試合追加に失敗しました: ' + err.message);
    } finally {
      setAddingGame(false);
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (!window.confirm('この試合を削除しますか？S3ファイルも削除されます。')) return;
    try {
      await api.deleteDuelGame(gameId);
      await load();
    } catch (err) {
      alert('削除に失敗しました: ' + err.message);
    }
  };

  const versionLabel = (v) => {
    const ver = `v${v.version_no ?? v.version}`;
    const date = formatServerDate(v.created_at);
    return [ver, v.description, date].filter(Boolean).join('  ');
  };

  const handleGameStatusChange = async (gameUuid, next) => {
    try {
      await api.duel('SetDuelStatus', { game_uuid: gameUuid, status: next });
      setDuel(prev => ({
        ...prev,
        games: prev.games.map(g => g.game_uuid === gameUuid ? { ...g, status: next } : g),
      }));
    } catch (err) {
      alert('ステータス更新に失敗しました: ' + err.message);
    }
  };

  if (!duel) return <div className="page"><p className="loading">読み込み中...</p></div>;

  return (
    <div className="page">
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => navigate('/duels')}>← 一覧に戻る</button>
      </div>

      <div className="card">
        <h2 className="card-title" style={{ marginBottom: 16 }}>基本情報</h2>

        <div className="form-group">
          <label className="form-label">デュエル名</label>
          <input className="form-input" value={name} onChange={e => setName(e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">概要</label>
          <textarea
            className="form-input"
            value={summary}
            onChange={e => setSummary(e.target.value)}
            rows={2}
            style={{ resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>

        <div className="form-group">
          <label className="form-label">説明</label>
          <textarea
            className="form-input"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={6}
            style={{ resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>

      <DuelSeriesSourceCard duel={duel} duelId={Number(duelsId)} onUploaded={load} />

      {/* ゲーム一覧 */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 className="card-title" style={{ margin: 0, flex: 1 }}>試合一覧</h2>
          <button className="btn btn-primary btn-sm" onClick={handleAddGame} disabled={addingGame}>
            {addingGame ? '追加中...' : '＋ 試合を追加'}
          </button>
        </div>

        {(duel?.games ?? []).length === 0 && (
          <p style={{ color: '#555', fontSize: '0.85rem' }}>試合がありません</p>
        )}

        {(duel?.games ?? []).map(game => (
          <div key={game.id} style={{ borderTop: '1px solid #1e1e1e', paddingTop: 12, marginTop: 12 }}>
            {/* Game番号＋レビュー印＋状態select＋入力/削除で 400px を超える */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
              <span style={{ fontWeight: 'bold', color: '#aaa' }}>Game {game.game_number}</span>
              <span
                className={`badge ${game.speech_review_cut_overview ? 'badge-active' : 'badge-pending'}`}
                style={{ fontSize: '0.7rem' }}
              >
                {game.speech_review_cut_overview ? 'レビュー済み' : '未レビュー'}
              </span>
              <select
                className="form-select"
                value={game.status}
                onChange={e => handleGameStatusChange(game.game_uuid, e.target.value)}
                style={{ width: 'auto', fontSize: '0.8rem', padding: '2px 6px', height: 'auto' }}
              >
                {DUEL_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button
                  className="btn btn-primary btn-sm"
                  title="再現エンジンの入力画面（スマホ縦基準。上は表示だけ、下のタブで再生・応答・登録。Issue #467）"
                  onClick={() => navigate(`/duels/${duelsId}/game/${game.game_uuid}/input`)}
                >入力</button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteGame(game.id)}
                >削除</button>
              </div>
            </div>
            <h3 style={{ fontSize: '0.85rem', color: '#777', marginBottom: 8 }}>動画</h3>
            {[1, 2, 3].map(i => (
              <MovieSlot key={i} index={i} game={game} duelId={Number(duelsId)} onUploaded={load} />
            ))}
            <HandExportPanel gameId={game.id} />
            <FixChatPanel gameUuid={game.game_uuid} />
          </div>
        ))}
      </div>

      <div className="card">
        <h2 className="card-title" style={{ marginBottom: 16 }}>対戦情報</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 16 }}>
          <PlayerCard info={duel.player_1_info} deckInfo={duel.player1_deck_info} onDeckClick={setDeckViewId} />
          <span style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#888', flexShrink: 0 }}>VS</span>
          <PlayerCard info={duel.player_2_info} deckInfo={duel.player2_deck_info} onDeckClick={setDeckViewId} />
        </div>

        {/* デッキ変更 */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          {[
            { label: duel.player_1_info?.kanji || duel.player_1_id, playerId: duel.player_1_id, deckId: deck1Id, setDeckId: setDeck1Id, setPinnedId: setDeck1PinnedId, disabled: savingDecks },
            { label: duel.player_2_info?.kanji || duel.player_2_id, playerId: duel.player_2_id, deckId: deck2Id, setDeckId: setDeck2Id, setPinnedId: setDeck2PinnedId, disabled: savingDecks },
          ].map(({ label, playerId, deckId, setDeckId, setPinnedId, disabled }, idx) => {
            const sortDecks = arr => arr.slice().sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'ja'));
            const playerDecks = sortDecks(decks.filter(d => d.player === playerId));
            const otherPlayers = [...new Set(decks.filter(d => d.player !== playerId && d.player).map(d => d.player))];
            return (
              <div key={idx} className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 180 }}>
                <label className="form-label">{label} のデッキ</label>
                <select className="form-select" value={deckId} disabled={disabled}
                  onChange={e => { setDeckId(e.target.value); setPinnedId(''); }}>
                  <option value="">— 未選択 —</option>
                  {playerDecks.length > 0 && (
                    <optgroup label={label}>
                      {playerDecks.map(d => <option key={d.id} value={d.parent ?? d.id}>{d.name}</option>)}
                    </optgroup>
                  )}
                  {otherPlayers.map(p => (
                    <optgroup key={p} label={p}>
                      {sortDecks(decks.filter(d => d.player === p)).map(d => <option key={d.id} value={d.parent ?? d.id}>{d.name}</option>)}
                    </optgroup>
                  ))}
                  {decks.filter(d => !d.player).length > 0 && (
                    <optgroup label="その他">
                      {sortDecks(decks.filter(d => !d.player)).map(d => <option key={d.id} value={d.parent ?? d.id}>{d.name}</option>)}
                    </optgroup>
                  )}
                </select>
              </div>
            );
          })}
        </div>

        {/* バージョン指定 */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 180 }}>
            <label className="form-label" style={{ color: '#888' }}>P1 バージョン</label>
            <select className="form-select" value={deck1PinnedId}
              onChange={e => setDeck1PinnedId(e.target.value)}
              disabled={savingDecks || !deck1Id}>
              <option value="">最新（保存したときの最新に固定）</option>
              {deck1Versions.map(v => <option key={v.id} value={v.id}>{versionLabel(v)}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 180 }}>
            <label className="form-label" style={{ color: '#888' }}>P2 バージョン</label>
            <select className="form-select" value={deck2PinnedId}
              onChange={e => setDeck2PinnedId(e.target.value)}
              disabled={savingDecks || !deck2Id}>
              <option value="">最新（保存したときの最新に固定）</option>
              {deck2Versions.map(v => <option key={v.id} value={v.id}>{versionLabel(v)}</option>)}
            </select>
          </div>
        </div>

        {(deck1PreviewCards || deck2PreviewCards) && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
            {[
              { cards: deck1PreviewCards, label: duel.player_1_info?.kanji || duel.player_1_id },
              { cards: deck2PreviewCards, label: duel.player_2_info?.kanji || duel.player_2_id },
            ].map(({ cards, label }, idx) => cards && (
              <div key={idx} style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: 4 }}>{label} ({cards.length}枚)</div>
                <div className="no-callout" style={{ display: 'grid', gridTemplateColumns: coarse ? 'repeat(auto-fill, minmax(64px, 1fr))' : 'repeat(10, 1fr)', gap: 2 }}>
                  {cards.map((card, i) => (
                    <div key={i} title={card.card_name}>
                      {card.thumbnail_url
                        ? <img src={card.thumbnail_url} alt={card.card_name} style={{ width: '100%', display: 'block', borderRadius: 2 }} />
                        : <div style={{ aspectRatio: '59/80', background: '#333', borderRadius: 2 }} />
                      }
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={handleSaveDecks} disabled={savingDecks}>
            {savingDecks ? '保存中...' : 'デッキを更新'}
          </button>
        </div>
      </div>

      <DeckViewModal deckId={deckViewId} onClose={() => setDeckViewId(null)} />

      {/* ソースファイル（ゲームごと） */}
      {(duel?.games ?? []).map(game => (
        <div key={`src-${game.id}`} className="card">
          <h2 className="card-title" style={{ marginBottom: 4 }}>
            Game {game.game_number} ソースファイル
          </h2>
          <p style={{ fontSize: '0.8rem', color: '#555', marginBottom: 12 }}>
            撮影した動画ファイルのバックアップ。フォルダ構造ごとアップロードできます。
          </p>
          <SourceUpload
            sources={game.sources}
            prefix={`Duels/${duelsId}/${game.id}/sources/`}
            requestUploadUrls={(filenames) =>
              api.project('UploadDuelSources', {
                duel_id: Number(duelsId), game_id: game.id, filenames,
              })}
            createMultipart={(filename) =>
              api.project('CreateMultipartUpload', {
                duel_id: Number(duelsId), game_id: game.id, filename,
              })}
            onUploaded={load}
          />
        </div>
      ))}
    </div>
  );
}
